import React, { forwardRef, useRef, useCallback, type CSSProperties } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../../utils/cn';
import type { ButtonProps } from './button.types';
import { ActivityIndicator } from '../activity-indicator';

// Helper to flatten style arrays (mobile compatibility)
const flattenStyle = (style?: CSSProperties | CSSProperties[]): CSSProperties | undefined => {
    if (!style) return undefined;
    if (Array.isArray(style)) {
        return style.reduce<CSSProperties>((acc, s) => (s ? { ...acc, ...s } : acc), {});
    }
    return style;
};

const buttonVariants = cva(
    'inline-flex items-center justify-center gap-2 rounded-[20px] font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50',
    {
        variants: {
            variant: {
                contained: 'text-[var(--color-neutral-0)] shadow-sm hover:shadow-md',
                outlined: 'border-2 bg-transparent hover:bg-opacity-10',
                text: 'bg-transparent hover:bg-opacity-10',
                elevated: 'shadow-md hover:shadow-lg',
                'contained-tonal': 'shadow-sm',
            },
            colorRole: {
                primary: '',
                secondary: '',
                tertiary: '',
                destructive: '',
            },
            colorMode: {
                light: '',
                dark: '',
                ghost: 'bg-transparent border-0',
            },
            size: {
                small: 'h-8 px-3 text-sm',
                medium: 'h-10 px-4 text-base',
                large: 'h-12 px-6 text-lg',
            },
            fullWidth: {
                true: 'w-full',
            },
        },
        compoundVariants: [
            // Primary color variants
            {
                variant: 'contained',
                colorRole: 'primary',
                className: 'bg-[var(--color-primary-100)] hover:bg-[var(--color-primary-90)]',
            },
            {
                variant: 'outlined',
                colorRole: 'primary',
                className:
                    'border-[var(--color-primary-100)] text-[var(--color-primary-100)] hover:bg-[var(--color-primary-10)]',
            },
            {
                variant: 'text',
                colorRole: 'primary',
                className: 'text-[var(--color-primary-100)] hover:bg-[var(--color-primary-10)]',
            },
            {
                variant: 'elevated',
                colorRole: 'primary',
                className: 'bg-[var(--color-primary-100)] hover:bg-[var(--color-primary-90)]',
            },
            {
                variant: 'contained-tonal',
                colorRole: 'primary',
                className:
                    'bg-[var(--color-primary-20)] text-[var(--color-primary-100)] hover:bg-[var(--color-primary-30)]',
            },

            // Secondary color variants
            {
                variant: 'contained',
                colorRole: 'secondary',
                className: 'bg-[var(--color-secondary-100)] hover:bg-[var(--color-secondary-80)]',
            },
            {
                variant: 'outlined',
                colorRole: 'secondary',
                className:
                    'border-[var(--color-secondary-100)] text-[var(--color-secondary-100)] hover:bg-[var(--color-secondary-10)]',
            },
            {
                variant: 'text',
                colorRole: 'secondary',
                className: 'text-[var(--color-secondary-100)] hover:bg-[var(--color-secondary-10)]',
            },
            {
                variant: 'elevated',
                colorRole: 'secondary',
                className: 'bg-[var(--color-secondary-100)] hover:bg-[var(--color-secondary-80)]',
            },
            {
                variant: 'contained-tonal',
                colorRole: 'secondary',
                className:
                    'bg-[var(--color-secondary-20)] text-[var(--color-secondary-100)] hover:bg-[var(--color-secondary-40)]',
            },

            // Tertiary color variants
            {
                variant: 'contained',
                colorRole: 'tertiary',
                className:
                    'bg-[var(--color-tertiary-100)] text-[var(--color-neutral-100)] hover:bg-[var(--color-tertiary-70)]',
            },
            {
                variant: 'outlined',
                colorRole: 'tertiary',
                className:
                    'border-[var(--color-tertiary-100)] text-[var(--color-tertiary-100)] hover:bg-[var(--color-tertiary-10)]',
            },
            {
                variant: 'text',
                colorRole: 'tertiary',
                className: 'text-[var(--color-tertiary-100)] hover:bg-[var(--color-tertiary-10)]',
            },
            {
                variant: 'elevated',
                colorRole: 'tertiary',
                className:
                    'bg-[var(--color-tertiary-100)] text-[var(--color-neutral-100)] hover:bg-[var(--color-tertiary-70)]',
            },
            {
                variant: 'contained-tonal',
                colorRole: 'tertiary',
                className:
                    'bg-[var(--color-tertiary-10)] text-[var(--color-tertiary-100)] hover:bg-[var(--color-tertiary-30)]',
            },

            // Destructive color variants
            {
                variant: 'contained',
                colorRole: 'destructive',
                className: 'bg-[var(--color-error-50)] hover:bg-[var(--color-error-60)]',
            },
            {
                variant: 'outlined',
                colorRole: 'destructive',
                className:
                    'border-[var(--color-error-50)] text-[var(--color-error-50)] hover:bg-[var(--color-error-0)]',
            },
            {
                variant: 'text',
                colorRole: 'destructive',
                className: 'text-[var(--color-error-50)] hover:bg-[var(--color-error-0)]',
            },
            {
                variant: 'elevated',
                colorRole: 'destructive',
                className: 'bg-[var(--color-error-50)] hover:bg-[var(--color-error-60)]',
            },
            {
                variant: 'contained-tonal',
                colorRole: 'destructive',
                className: 'bg-[var(--color-error-0)] text-[var(--color-error-50)] hover:bg-[var(--color-error-20)]',
            },
        ],
        defaultVariants: {
            variant: 'contained',
            colorRole: 'primary',
            colorMode: 'light',
            size: 'medium',
            fullWidth: false,
        },
    }
);

export interface ButtonComponentProps
    extends
        ButtonProps,
        Omit<VariantProps<typeof buttonVariants>, 'colorRole' | 'colorMode' | 'variant' | 'size' | 'fullWidth'> {}

const LONG_PRESS_DELAY = 500; // ms

const Button = forwardRef<HTMLButtonElement, ButtonComponentProps>(
    (
        {
            className,
            variant,
            mode, // Mobile compatibility alias for variant
            colorRole = 'primary',
            colorMode = 'light',
            size = 'medium',
            fullWidth = false,
            loading = false,
            leftIcon,
            rightIcon,
            disabled,
            children,
            showIf = true,
            testID = 'button-test-id',
            onPress,
            onClick,
            onLongPress,
            style,
            contentStyle,
            labelStyle,
            contentClassName,
            loadingProps,
            ...props
        },
        ref
    ) => {
        // Long press support
        const longPressTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
        const isLongPress = useRef(false);

        const handleMouseDown = useCallback(() => {
            if (!onLongPress) return;
            isLongPress.current = false;
            longPressTimer.current = setTimeout(() => {
                isLongPress.current = true;
                onLongPress();
            }, LONG_PRESS_DELAY);
        }, [onLongPress]);

        const handleMouseUp = useCallback(() => {
            if (longPressTimer.current) {
                clearTimeout(longPressTimer.current);
                longPressTimer.current = null;
            }
        }, []);

        const handleMouseLeave = useCallback(() => {
            if (longPressTimer.current) {
                clearTimeout(longPressTimer.current);
                longPressTimer.current = null;
            }
        }, []);

        // Support both onPress (mobile) and onClick (web)
        const handleClick = useCallback(
            (e: React.MouseEvent<HTMLButtonElement>) => {
                // Don't trigger click if it was a long press
                if (isLongPress.current) {
                    isLongPress.current = false;
                    return;
                }
                (onPress || onClick)?.(e);
            },
            [onPress, onClick]
        );

        if (!showIf) return null;

        // Support mode (mobile) as alias for variant
        const finalVariant = mode || variant || 'contained';

        // Flatten styles for mobile compatibility
        const flatStyle = flattenStyle(style);
        const flatContentStyle = flattenStyle(contentStyle);
        const flatLabelStyle = flattenStyle(labelStyle);

        if (!showIf) return null;

        return (
            <button
                className={cn(
                    buttonVariants({ variant: finalVariant, colorRole, colorMode, size, fullWidth, className })
                )}
                ref={ref}
                disabled={disabled || loading}
                data-testid={testID}
                onClick={handleClick}
                onMouseDown={onLongPress ? handleMouseDown : undefined}
                onMouseUp={onLongPress ? handleMouseUp : undefined}
                onMouseLeave={onLongPress ? handleMouseLeave : undefined}
                onTouchStart={onLongPress ? handleMouseDown : undefined}
                onTouchEnd={onLongPress ? handleMouseUp : undefined}
                style={{ ...flatStyle, ...flatContentStyle }}
                {...props}
            >
                <ActivityIndicator {...loadingProps} showIf={loading} />
                {!loading && leftIcon && <span className="flex-shrink-0">{leftIcon}</span>}
                {children && (
                    <span className={contentClassName} style={flatLabelStyle}>
                        {children}
                    </span>
                )}
                {!loading && rightIcon && <span className="flex-shrink-0">{rightIcon}</span>}
            </button>
        );
    }
);

Button.displayName = 'Button';

export default Button;
